import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { Send, Loader2, Volume2, Mic } from "lucide-react";
import { streamChat, speak, transcribe } from "../../lib/api";
import Markdown from "./Markdown";
import { ARIA } from "../../lib/assets";

const SUGGESTIONS = [
  "Draft a warm reply declining a meeting, in my style",
  "Research a company before my meeting tomorrow",
  "Plan a 3-day work trip to Mumbai and build the itinerary",
  "Give me a plan to launch a new product — strategy, HR and legal",
];

function StageChip({ stage }) {
  const done = stage.status === "done";
  const color = stage.color || "#E2B857";
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap border transition-all"
      style={{ borderColor: color + (done ? "55" : "aa"), background: color + "1a", color }}
      data-testid={`pipeline-stage-${stage.key}`}>
      {done ? <span>✓</span> : <span className="w-1.5 h-1.5 rounded-full dot-pulse" style={{ background: color }} />}
      {stage.label}
    </div>
  );
}

const AriaConsole = forwardRef(({ language, onDecision, name = "Kaustubh", pending, voice = "nova", speed = 1.0 }, ref) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [stages, setStages] = useState([]);
  const [plan, setPlan] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [speakingId, setSpeakingId] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recBusy, setRecBusy] = useState(false);
  const sessionId = useRef("aria-" + Math.random().toString(36).slice(2));
  const scrollRef = useRef(null);
  const audioRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const hour = new Date().getHours();
  const period = hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, stages, plan]);

  const send = async (text, agentKey = "chief_of_staff") => {
    const msg = (text || "").trim();
    if (!msg || streaming) return;
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setInput("");
    setStages([]);
    setPlan(null);
    setStreaming(true);
    try {
      await streamChat({ message: msg, session_id: sessionId.current, language, agent: agentKey }, (ev) => {
        if (ev.type === "stage") {
          setStages((prev) => {
            const i = prev.findIndex((s) => s.key === ev.key);
            if (i >= 0) { const c = [...prev]; c[i] = { ...c[i], ...ev }; return c; }
            return [...prev, ev];
          });
        } else if (ev.type === "plan") {
          setPlan(ev);
        } else if (ev.type === "token") {
          setMessages((m) => {
            const c = [...m];
            c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + ev.content };
            return c;
          });
        } else if (ev.type === "decision") {
          onDecision && onDecision(ev.decision);
        } else if (ev.type === "error") {
          setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "⚠️ " + ev.message }; return c; });
        }
      });
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Connection error. Please try again." }; return c; });
    }
    setStreaming(false);
  };

  useImperativeHandle(ref, () => ({ send }));

  useEffect(() => {
    if (pending && pending.text) send(pending.text, pending.agent || "chief_of_staff");
    // eslint-disable-next-line
  }, [pending?.n]);

  const playTTS = async (idx, text) => {
    try {
      setSpeakingId(idx);
      const langVoice = { hi: "shimmer", mr: "shimmer" }[language];
      const audio = await speak(text, langVoice || voice, speed);
      audioRef.current.src = audio;
      audioRef.current.onended = () => setSpeakingId(null);
      await audioRef.current.play();
    } catch (e) { setSpeakingId(null); }
  };

  const toggleMic = async () => {
    if (recording) { mediaRef.current && mediaRef.current.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false); setRecBusy(true);
        try {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          const text = await transcribe(blob, language === "auto" ? "" : language);
          if (text && text.trim()) send(text.trim());
        } catch (e) {}
        setRecBusy(false);
      };
      mediaRef.current = mr; mr.start(); setRecording(true);
    } catch (e) {}
  };

  const empty = messages.length === 0;

  return (
    <div className="flex flex-col" style={{ minHeight: "calc(100vh - 190px)" }} data-testid="aria-console">
      <audio ref={audioRef} className="hidden" />

      {empty ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-4 fade-up">
          <div className="relative">
            <img src={ARIA.portrait} alt="Aria" className="w-28 h-28 rounded-full object-cover border border-amber-400/40 shadow-[0_0_50px_-8px_rgba(226,184,87,0.7)]" data-testid="aria-avatar" />
            <span className="absolute bottom-1 right-1 w-4 h-4 rounded-full bg-emerald-400 border-2 border-[#0A0908]" />
          </div>
          <div className="overline mt-6">Your Executive Chief of Staff</div>
          <h1 className="font-display text-5xl sm:text-6xl font-light tracking-tight text-amber-50 mt-3 leading-[1.05]">
            Good {period}, <span className="italic text-amber-300/90">{name}.</span>
          </h1>
          <p className="text-slate-300/85 mt-4 text-base sm:text-lg font-light max-w-lg">
            I'm Aria. Tell me what you need — I'll coordinate your specialist office and bring you one clear answer.
          </p>
          <div className="mt-8 flex flex-wrap gap-2 justify-center max-w-2xl">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} className="ghost-btn rounded-full px-4 py-2 text-[13px]" data-testid="aria-suggestion">
                {s}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-5 pb-4">
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "assistant" && <img src={ARIA.portrait} alt="Aria" className="w-9 h-9 rounded-full object-cover border border-amber-400/30 mt-1 shrink-0" />}
              <div className={`max-w-[85%] rounded-2xl px-5 py-3.5 ${m.role === "user" ? "bg-amber-500/12 border border-amber-500/25 text-amber-50" : "glass-card"}`} data-testid={`aria-message-${m.role}`}>
                {m.role === "assistant" ? (
                  <>
                    {m.content ? <Markdown text={m.content} /> : streaming && i === messages.length - 1 ? (
                      <span className="text-slate-400 text-xs flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Aria is working…</span>
                    ) : null}
                    {m.content && !streaming && (
                      <button onClick={() => playTTS(i, m.content)} className="mt-2.5 flex items-center gap-1.5 text-[12px] text-amber-400/80 hover:text-amber-300 transition" data-testid="tts-play-button">
                        {speakingId === i ? <Loader2 size={12} className="animate-spin" /> : <Volume2 size={12} />} Read aloud
                      </button>
                    )}
                  </>
                ) : m.content}
              </div>
            </div>
          ))}
          {streaming && (plan || stages.length > 0) && (
            <div className="flex gap-3">
              <div className="w-9 shrink-0" />
              <div className="glass-card p-3.5 max-w-[85%] fade-up" data-testid="agent-pipeline">
                {plan?.plan && <div className="text-[12px] text-slate-400 mb-2.5 leading-snug font-light">{plan.plan}</div>}
                <div className="flex flex-wrap gap-1.5 items-center">
                  {stages.map((s) => <StageChip key={s.key} stage={s} />)}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="sticky bottom-0 pt-3">
        <div className="flex items-end gap-2 glass rounded-2xl p-2.5 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)]">
          <button onClick={toggleMic} className={`rounded-xl p-3 transition ${recording ? "text-rose-400 mic-live" : "text-amber-300/90 hover:bg-amber-500/10"}`} data-testid="aria-mic">
            {recBusy ? <Loader2 size={18} className="animate-spin" /> : <Mic size={18} />}
          </button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
            rows={1}
            placeholder="Speak or type — ask Aria anything…"
            className="flex-1 bg-transparent resize-none px-2 py-2.5 text-[15px] focus:outline-none max-h-40"
            data-testid="aria-input"
          />
          <button onClick={() => send(input)} disabled={streaming || !input.trim()} className="gold-btn rounded-xl p-3 disabled:opacity-40" data-testid="aria-send">
            {streaming ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
});

AriaConsole.displayName = "AriaConsole";
export default AriaConsole;
